import type { Provenance } from './types';

/**
 * How much a number is worth as a record of what happened.
 *
 * `CLAUDE.md` requires provenance to travel with every stored number and to be
 * shown wherever it is. Doing that means the UI sometimes has one figure built
 * from several days of different kinds, and it has to say something about the
 * whole. This is the ordering that lets it.
 *
 * It runs from what somebody recorded to what the engine worked out:
 *
 *  - `measured` — real minutes, actually counted.
 *  - `coach_observed` — the coach watched it and said so.
 *  - `player_reported` — the player said so. Honest, and nobody else saw it.
 *  - `derived` — computed from another number that was recorded.
 *  - `estimated_from_plan` — computed from a plan, which may not have happened.
 *
 * The product deliberately collects no player-reported RPE (D-006), so that
 * rung is unused today and kept because the column accepts it.
 */
export const PROVENANCE_STRENGTH: Record<Provenance, number> = {
  measured: 5,
  coach_observed: 4,
  player_reported: 3,
  derived: 2,
  estimated_from_plan: 1,
};

/**
 * The weakest of several, which is what a figure built from all of them is
 * worth.
 *
 * Not the commonest, and not an average. Nine measured days and one estimate is
 * not a measured week — a coach who reads it as one has been told something
 * false, which is the thing this whole constraint exists to prevent.
 *
 * An empty list reads as the weakest case rather than the strongest: a number
 * with nothing behind it is not a measurement.
 */
export function weakest(values: readonly Provenance[]): Provenance {
  // Seeded from the first value, not from the weakest kind: starting at the
  // floor means a list of nothing but measured days never beats the seed and
  // comes back as an estimate.
  const [first] = values;
  if (first === undefined) return 'estimated_from_plan';

  let worst = first;
  for (const value of values) {
    if (PROVENANCE_STRENGTH[value] < PROVENANCE_STRENGTH[worst]) worst = value;
  }
  return worst;
}

/** How many days of each kind, so a screen can say how mixed a figure is. */
export function countProvenance(
  values: readonly Provenance[],
): Partial<Record<Provenance, number>> {
  const counts: Partial<Record<Provenance, number>> = {};
  for (const value of values) counts[value] = (counts[value] ?? 0) + 1;
  return counts;
}
